import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

// Helper: pull a string out of any code-list shape (string, dict, list).
function firstCode(items) {
  if (!items) return '';
  if (Array.isArray(items)) {
    if (!items.length) return '';
    const h = items[0];
    return typeof h === 'object' ? String(h?.code || h?.value || h?.text || '') : String(h);
  }
  if (typeof items === 'object') {
    return String(items?.code || items?.value || items?.text || '');
  }
  return String(items);
}

// Owns ALL EHR-push state and side effects. Parent passes the data
// dependencies (transcript / notes / session metadata / user / custom EHRs)
// because the push payload is assembled from across the page.
export function useEhrPush({
  sessionId,
  token,
  user,
  transcript,
  sessionData,
  medicalNote,
  psychotherapyNote,
  diagnosis,
  customEhrs,
}) {
  const [ehrDialogOpen, setEhrDialogOpen] = useState(false);
  const [ehrPushing, setEhrPushing] = useState(false);
  const [ehrPushResult, setEhrPushResult] = useState(null);
  const [ehrSystem, setEhrSystem] = useState(null);
  const [tebraStatus, setTebraStatus] = useState(null);
  const [testingTebra, setTestingTebra] = useState(false);
  const [ehrOptions, setEhrOptions] = useState({
    include_medical_note: true,
    include_psychotherapy_note: true,
    include_diagnosis: false,
    include_billing_codes: true,
    include_transcript: false,
  });
  const [ehrPreview, setEhrPreview] = useState(null);
  const [ehrPushHistory, setEhrPushHistory] = useState([]);
  const [tebraEhrData, setTebraEhrData] = useState(null);

  // Live-detected EHR tabs (from the Pinnacle browser extension)
  const [detectedEHRs, setDetectedEHRs] = useState([]);
  const [extensionActive, setExtensionActive] = useState(false);
  // { [ehr_name]: 'checking' | 'ready' | 'needs-refresh' | 'error' }
  const [ehrHealth, setEhrHealth] = useState({});

  // Template state — applies across any detected EHR (not just Tebra)
  const [tebraTemplates, setTebraTemplates] = useState(null);
  const [tebraTemplateKey, setTebraTemplateKey] = useState('');
  const [tebraAutoDetected, setTebraAutoDetected] = useState(null);
  const [tebraPreviewSections, setTebraPreviewSections] = useState(null);
  const [tebraPreviewLoading, setTebraPreviewLoading] = useState(false);
  const [tebraExpanded, setTebraExpanded] = useState({});
  const [sessionMeta, setSessionMeta] = useState(null);
  const [tebraFullTemplateNote, setTebraFullTemplateNote] = useState('');
  const [tebraTemplateSearch, setTebraTemplateSearch] = useState('');

  const tebraCurrentTemplate = tebraTemplates?.find((t) => t.key === tebraTemplateKey) || null;
  const TEBRA_SECTION_ORDER = tebraCurrentTemplate?.labels || [];

  // ─── Live EHR detection (extension postMessage handshake) ───────────
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.source !== window) return;
      if (event.data?.type === 'PINNACLE_LIVE_EHRS_RESPONSE') {
        setDetectedEHRs(event.data.liveEHRs || []);
        setExtensionActive(event.data.extensionActive || false);
      }
    };
    window.addEventListener('message', handleMessage);

    const requestEHRs = () => {
      window.postMessage({ type: 'PINNACLE_REQUEST_LIVE_EHRS' }, '*');
    };
    requestEHRs();
    const interval = setInterval(requestEHRs, 3000);
    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(interval);
    };
  }, []);

  // ─── Health-check each detected EHR tab while the dialog is open ───
  useEffect(() => {
    if (!ehrDialogOpen || detectedEHRs.length === 0) return undefined;

    const pingAll = () => {
      detectedEHRs.forEach((ehr) => {
        if (!ehr.tab_id) return;
        const requestId = 'ping-' + ehr.tab_id + '-' + Date.now();
        setEhrHealth((prev) => ({ ...prev, [ehr.ehr_name]: prev[ehr.ehr_name] || 'checking' }));

        const handler = (event) => {
          if (event.source !== window) return;
          if (event.data?.type !== 'PINNACLE_PING_EHR_TAB_RESPONSE') return;
          if (event.data.requestId !== requestId) return;
          window.removeEventListener('message', handler);
          clearTimeout(timeoutId);
          setEhrHealth((prev) => ({
            ...prev,
            [ehr.ehr_name]: event.data.status === 'ready' ? 'ready'
              : event.data.status === 'needs-refresh' ? 'needs-refresh'
                : 'error',
          }));
        };
        const timeoutId = setTimeout(() => {
          window.removeEventListener('message', handler);
          setEhrHealth((prev) => ({ ...prev, [ehr.ehr_name]: 'error' }));
        }, 5000);
        window.addEventListener('message', handler);

        window.postMessage({
          type: 'PINNACLE_PING_EHR_TAB',
          requestId,
          tabId: ehr.tab_id,
          autoInject: true,
        }, '*');
      });
    };

    pingAll();
    const interval = setInterval(pingAll, 15000);
    return () => clearInterval(interval);
  }, [ehrDialogOpen, detectedEHRs]);

  // ─── Auto-select first detected EHR when dialog opens ──────────────
  useEffect(() => {
    if (!ehrDialogOpen || ehrSystem || detectedEHRs.length === 0) return;
    const preferred = detectedEHRs.find((e) => e.is_active) || detectedEHRs[0];
    if (!preferred) return;
    setEhrSystem(`extension:${preferred.ehr_name}`);
  }, [ehrDialogOpen, detectedEHRs, ehrSystem]);

  // ─── Build a values-dict for note_format placeholder substitution ─
  const buildPlaceholderValues = () => {
    const sessionDate = (sessionData?.created_at || '').slice(0, 10);
    const patientName = sessionMeta?.patient || '';
    return {
      subjective: medicalNote?.subjective || medicalNote?.chief_complaint || medicalNote?.history_present_illness || '',
      objective: medicalNote?.objective || psychotherapyNote?.mental_status_exam || psychotherapyNote?.client_mood_affect || '',
      assessment: medicalNote?.assessment || '',
      plan: medicalNote?.plan || '',
      diagnosis_text: diagnosis?.diagnosis || diagnosis?.primary_diagnosis || medicalNote?.diagnosis_summary || '',
      icd10_primary: firstCode(diagnosis?.icd10_codes) || firstCode(medicalNote?.icd10_codes) || '',
      cpt_code: firstCode(diagnosis?.cpt_codes) || firstCode(medicalNote?.cpt_codes) || medicalNote?.cpt_code || '',
      patient_name: patientName,
      session_date: sessionDate,
      session_duration: String(sessionMeta?.duration || sessionData?.duration_minutes || sessionData?.duration || ''),
      therapy_interventions: psychotherapyNote?.interventions || psychotherapyNote?.therapeutic_interventions || '',
      provider_name: user?.full_name || user?.email || '',
    };
  };

  // ─── Load Tebra preview sections for the current template ──────────
  const loadTebraPreview = async (force = false, templateKey = tebraTemplateKey) => {
    if (!templateKey) return;
    if (!force && tebraPreviewSections && tebraCurrentTemplate?.key === templateKey) return;

    if (templateKey.startsWith('practice_ehr:')) {
      const tpl = (tebraTemplates || []).find((t) => t.key === templateKey);
      const noteFormat = tpl?.note_format || '';
      const values = buildPlaceholderValues();
      const rendered = noteFormat.replace(
        /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
        (m, key) => (key in values ? values[key] : m),
      );
      setTebraPreviewSections({ Note: rendered });
      setTebraFullTemplateNote(rendered);
      setTebraPreviewLoading(false);
      return;
    }

    setTebraPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/ehr/tebra-ehr-format/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { template: templateKey },
      });
      const sections = res.data?.tebra_sections || {};
      const labels = res.data?.tebra_template_labels || tebraCurrentTemplate?.labels || [];
      if (res.data?.session_meta) setSessionMeta(res.data.session_meta);
      const normalized = {};
      labels.forEach((k) => { normalized[k] = sections[k] || ''; });
      setTebraPreviewSections(normalized);
      setTebraFullTemplateNote(res.data?.full_template_note || '');
    } catch (err) {
      toast.error('Could not load Tebra section preview');
    } finally {
      setTebraPreviewLoading(false);
    }
  };

  const reloadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/ehr/tebra-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTebraTemplates(res.data?.templates || []);
    } catch {
      toast.error('Could not load note template list');
    }
  };

  // Lazy-load templates when push dialog first opens
  useEffect(() => {
    if (!ehrDialogOpen || tebraTemplates) return;
    reloadTemplates();
  }, [ehrDialogOpen, tebraTemplates, token]);

  // Eagerly load templates on mount so the second copy block is usable
  useEffect(() => {
    if (!tebraTemplates && token) reloadTemplates();
  }, [token]);

  // Ask the extension to auto-detect the open Tebra template
  useEffect(() => {
    if (!ehrDialogOpen || ehrSystem !== 'extension:Tebra') return undefined;
    const targetEhr = detectedEHRs.find((e) => e.ehr_name === 'Tebra');
    if (!targetEhr?.tab_id) return undefined;

    const requestId = 'tpl-detect-' + Date.now();
    const handler = (event) => {
      if (event.source !== window) return;
      if (event.data?.type !== 'PINNACLE_DETECT_TEBRA_TEMPLATE_RESPONSE') return;
      if (event.data.requestId !== requestId) return;
      window.removeEventListener('message', handler);
      if (event.data.templateKey) {
        setTebraAutoDetected(event.data.templateKey);
        setTebraTemplateKey((prev) => prev || event.data.templateKey);
      }
    };
    window.addEventListener('message', handler);
    window.postMessage({
      type: 'PINNACLE_DETECT_TEBRA_TEMPLATE',
      requestId,
      tabId: targetEhr.tab_id,
    }, '*');
    const t = setTimeout(() => window.removeEventListener('message', handler), 4000);
    return () => { clearTimeout(t); window.removeEventListener('message', handler); };
  }, [ehrDialogOpen, ehrSystem, detectedEHRs]);

  // Load preview when template changes inside the open dialog
  useEffect(() => {
    if (ehrDialogOpen && ehrSystem?.startsWith('extension:') && tebraTemplateKey) {
      loadTebraPreview(true, tebraTemplateKey);
    }
  }, [ehrDialogOpen, ehrSystem, tebraTemplateKey]);

  // Auto-load templated note whenever the provider picks/changes a template
  useEffect(() => {
    if (!tebraTemplateKey) {
      setTebraFullTemplateNote('');
      return;
    }
    loadTebraPreview(true, tebraTemplateKey);
  }, [tebraTemplateKey, sessionId]);

  // Live-refresh the Push Preview whenever options/template change
  useEffect(() => {
    if (!ehrDialogOpen) return undefined;
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/ehr/push-preview`, {
          session_id: sessionId,
          ...ehrOptions,
          template_key: tebraTemplateKey || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        setEhrPreview(res.data);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [ehrDialogOpen, ehrOptions, tebraTemplateKey, sessionId, token]);

  // Broadcast template selection to the extension
  useEffect(() => {
    if (!tebraCurrentTemplate) {
      window.postMessage({ type: 'PINNACLE_TEMPLATE_SELECTED', key: null }, '*');
      return;
    }
    window.postMessage({
      type: 'PINNACLE_TEMPLATE_SELECTED',
      key: tebraCurrentTemplate.key,
      name: tebraCurrentTemplate.name,
      labels: tebraCurrentTemplate.labels || [],
      is_custom: !!tebraCurrentTemplate.is_custom,
    }, '*');
  }, [tebraCurrentTemplate]);

  // Build the metadata header block prepended to every template-based push.
  const buildMetadataHeader = () => {
    const meta = sessionMeta || {};
    const patient = meta.patient || sessionData?.patient_name || transcript?.patient_name || '';
    const date = meta.date || sessionData?.session_date || (sessionData?.created_at || '').slice(0, 10) || '';
    const time = meta.time || (sessionData?.start_time
      ? `${sessionData.start_time}${sessionData.end_time ? ` — ${sessionData.end_time}` : ''} EST`
      : '');
    const duration = meta.duration
      || (sessionData?.duration_seconds
        ? `${Math.floor(sessionData.duration_seconds / 60)}m ${sessionData.duration_seconds % 60}s`
        : '');
    const sessionType = meta.session_type || sessionData?.session_type || '';
    return [
      `Patient: ${patient}`,
      `Date: ${date}`,
      `Time: ${time}`,
      `Duration: ${duration}`,
      `Session Type: ${sessionType}`,
    ].join('\n');
  };

  const handleOpenEhrPush = async () => {
    setEhrDialogOpen(true);
    setEhrPushResult(null);
    try {
      const res = await axios.get(`${API}/ehr/push-history/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEhrPushHistory(res.data);
    } catch {}
    try {
      const res = await axios.post(`${API}/ehr/push-preview`, {
        session_id: sessionId,
        ...ehrOptions,
        template_key: tebraTemplateKey || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEhrPreview(res.data);
    } catch {}
    if (!tebraStatus) {
      try {
        const res = await axios.get(`${API}/ehr/tebra/test-connection`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTebraStatus(res.data);
      } catch {}
    }
    try {
      const res = await axios.get(`${API}/ehr/universal-format/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTebraEhrData(res.data);
    } catch {
      try {
        const res = await axios.get(`${API}/ehr/tebra-ehr-format/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTebraEhrData(res.data);
      } catch {}
    }
  };

  const handleTestTebra = async () => {
    setTestingTebra(true);
    try {
      const res = await axios.get(`${API}/ehr/tebra/test-connection`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTebraStatus(res.data);
      if (res.data.connected) {
        setEhrSystem('tebra');
        toast.success('Tebra connected!');
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error('Connection test failed');
    } finally {
      setTestingTebra(false);
    }
  };

  const handleEhrPush = async () => {
    if (ehrSystem?.startsWith('extension:')) {
      return handleExtensionPush();
    }
    setEhrPushing(true);
    setEhrPushResult(null);
    try {
      const res = await axios.post(`${API}/ehr/push`, {
        session_id: sessionId,
        ...ehrOptions,
        ehr_system: ehrSystem,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEhrPushResult(res.data);
      toast.success(res.data.message);
      const hist = await axios.get(`${API}/ehr/push-history/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEhrPushHistory(hist.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Push failed');
      setEhrPushResult({ status: 'error', message: 'Push failed' });
    } finally {
      setEhrPushing(false);
    }
    return undefined;
  };

  // Real-time one-click push via the extension. PRESERVED VERBATIM from the
  // pre-refactor TranscriptViewer so the intentional checkbox-filter quirk
  // and Tebra fallback paths stay byte-identical.
  const handleExtensionPush = async () => {
    const targetEhrName = ehrSystem.split(':')[1];
    const targetEhr = detectedEHRs.find((e) => e.ehr_name === targetEhrName);

    let data = tebraEhrData;
    if (!data) {
      try {
        const res = await axios.get(`${API}/ehr/universal-format/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        data = res.data;
        setTebraEhrData(data);
      } catch {
        toast.error('Could not load note content for push');
        return;
      }
    }

    let tebraSections = null;
    if (tebraTemplateKey) {
      if (tebraPreviewSections) {
        tebraSections = tebraPreviewSections;
      } else {
        try {
          const res = await axios.get(`${API}/ehr/tebra-ehr-format/${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { template: tebraTemplateKey },
          });
          tebraSections = res.data?.tebra_sections || null;
        } catch (e) {
          // Non-fatal — generic fallback below will still work
        }
      }
    } else if (targetEhrName === 'Tebra') {
      toast.error('Pick a Tebra note template first (dropdown in the preview panel).');
      return;
    }

    const sections = {};
    if (ehrOptions.include_medical_note && data.sections) {
      if (data.sections.subjective) sections.subjective = data.sections.subjective;
      if (data.sections.objective) sections.objective = data.sections.objective;
      if (data.sections.assessment) sections.assessment = data.sections.assessment;
      if (data.sections.plan) sections.plan = data.sections.plan;
    }
    if (ehrOptions.include_psychotherapy_note && data.sections?.psychotherapy_note) {
      sections.psychotherapy_note = data.sections.psychotherapy_note;
    }
    if (ehrOptions.include_diagnosis && data.sections?.diagnosis) {
      sections.diagnosis = data.sections.diagnosis;
    }
    if (ehrOptions.include_billing_codes && data.sections?.billing) {
      sections.billing = data.sections.billing;
    }
    if (ehrOptions.include_transcript && transcript?.edited_text) {
      sections.transcript = transcript.edited_text;
    }

    const buildFilteredSections = () => {
      const out = [];
      if (ehrOptions.include_medical_note && data.sections) {
        const medParts = [];
        if (data.sections.subjective) medParts.push(`Subjective: ${data.sections.subjective}`);
        if (data.sections.objective) medParts.push(`Objective: ${data.sections.objective}`);
        if (data.sections.assessment) medParts.push(`Assessment: ${data.sections.assessment}`);
        if (data.sections.plan) medParts.push(`Plan: ${data.sections.plan}`);
        if (medParts.length) out.push(['Medical Note (SOAP)', medParts.join('\n\n')]);
      }
      if (ehrOptions.include_psychotherapy_note && data.sections?.psychotherapy_note) {
        out.push(['Psychotherapy Note', data.sections.psychotherapy_note]);
      }
      if (ehrOptions.include_diagnosis && data.sections?.diagnosis) {
        out.push(['Comprehensive Diagnostic Evaluation', data.sections.diagnosis]);
      }
      if (ehrOptions.include_billing_codes && data.sections?.billing) {
        out.push(['Billing Codes', data.sections.billing]);
      }
      if (ehrOptions.include_transcript && transcript?.edited_text) {
        out.push(['Session Transcript', transcript.edited_text]);
      }
      return out;
    };

    let fullContent = '';
    if (tebraSections && tebraCurrentTemplate?.labels?.length) {
      const orderedLabels = tebraCurrentTemplate.labels;
      const isTebraTarget = targetEhrName === 'Tebra';
      const header = isTebraTarget ? '' : buildMetadataHeader();
      const headerWithTpl = isTebraTarget ? '' : header + `\nTemplate: ${tebraCurrentTemplate.name}`;
      const body = orderedLabels
        .map((label) => {
          let val = (tebraSections[label] || '').trim();
          if (!isTebraTarget && (label === 'Telehealth consent/ clinician time' || label === 'Telehealth consent')) {
            val = 'Telehealth consent obtained verbally at the start of the visit.';
          }
          return val ? `${label}:\n${val}` : null;
        })
        .filter(Boolean)
        .join('\n\n');

      const trailing = buildFilteredSections()
        .map(([title, content]) => `${title}:\n${content}`)
        .join('\n\n');
      const trailingSection = trailing ? `\n\n${trailing}` : '';
      fullContent = [headerWithTpl, body].filter(Boolean).join('\n\n') + trailingSection;
    } else {
      const header = buildMetadataHeader();
      const bodyBlocks = buildFilteredSections().map(([title, content]) => `${title}:\n${content}`);
      if (bodyBlocks.length === 0) {
        fullContent = data.full_formatted_note || '';
      } else {
        fullContent = [header, bodyBlocks.join('\n\n')].filter(Boolean).join('\n\n');
      }
    }

    let matchedCustomEhr = null;
    const pickedPracticeTpl = tebraTemplateKey?.startsWith?.('practice_ehr:')
      ? (tebraTemplates || []).find((t) => t.key === tebraTemplateKey)
      : null;
    if (pickedPracticeTpl?.note_format) {
      matchedCustomEhr = { name: pickedPracticeTpl.name, note_format: pickedPracticeTpl.note_format };
    } else {
      matchedCustomEhr = (customEhrs || []).find(
        (c) => c.name === targetEhrName || c._displayName === targetEhrName,
      );
    }
    if (matchedCustomEhr?.note_format) {
      const values = buildPlaceholderValues();
      fullContent = matchedCustomEhr.note_format.replace(
        /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
        (m, key) => (key in values ? values[key] : m),
      );
    }

    if (!Object.keys(sections).length && !fullContent) {
      toast.error('No content selected — enable at least one section');
      return;
    }

    setEhrPushing(true);
    setEhrPushResult(null);

    const requestId = 'pnp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    let timeoutId;

    const cleanup = () => {
      window.removeEventListener('message', handleResponse);
      clearTimeout(timeoutId);
    };

    const handleResponse = (event) => {
      if (event.source !== window) return;
      if (event.data?.type !== 'PINNACLE_PUSH_VIA_EXTENSION_RESPONSE') return;
      if (event.data.requestId !== requestId) return;
      cleanup();
      setEhrPushing(false);
      if (event.data.ok) {
        const filledDetail = event.data.filledSections;
        const method = event.data.method;
        let msg;
        if (method === 'tebra-sections' && Array.isArray(filledDetail) && filledDetail.length > 0) {
          msg = `Filled ${filledDetail.length} Tebra section(s): ${filledDetail.join(', ')}. Now click SAVE in Tebra to persist the note.`;
        } else if (method === 'tebra-sidebar-fallback') {
          msg = `Could not auto-locate Tebra chart fields. Opened side panel on the Tebra tab — paste each section into the correct field, then click Save.`;
        } else if (Array.isArray(filledDetail) && filledDetail.length > 0) {
          msg = `Note injected into ${event.data.targetEhrName || targetEhrName} (${filledDetail.length} section${filledDetail.length === 1 ? '' : 's'})`;
        } else {
          msg = `Note injected into ${event.data.targetEhrName || targetEhrName} in real-time`;
        }
        if (method === 'tebra-sidebar-fallback') {
          toast.warning(msg, { duration: 10000 });
        } else {
          toast.success(msg, { duration: 8000 });
        }
        setEhrPushResult({
          status: method === 'tebra-sidebar-fallback' ? 'warning' : 'success',
          message: msg,
          sections_pushed: filledDetail || Object.keys(sections),
        });
        try {
          window.postMessage({
            type: 'PINNACLE_RECORD_PUSH_OUTCOME',
            session_id: sessionId,
            patient_name: sessionData?.patient_name || transcript?.patient_name || '',
            session_type: sessionData?.session_type || '',
            ehr: targetEhrName,
            template: tebraCurrentTemplate?.name || '',
          }, '*');
        } catch (_) {}
        axios.post(`${API}/ehr/push-history`, {
          session_id: sessionId,
          ehr_system: `extension:${targetEhrName}`,
          sections_pushed: filledDetail || Object.keys(sections),
          status: method === 'tebra-sidebar-fallback' ? 'partial' : 'success',
        }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        axios.get(`${API}/ehr/push-history/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((h) => setEhrPushHistory(h.data)).catch(() => {});
      } else {
        const rawErr = event.data.error || 'Extension push failed';
        const needsRefresh = event.data.hint === 'refresh-required'
          || /receiving end|could not establish|no tab/i.test(rawErr);
        const friendly = needsRefresh
          ? `Please refresh your ${targetEhrName} tab (Ctrl+R / ⌘+R) and try again. The extension needs to reload on pages that were open before installation.`
          : rawErr;
        toast.error(friendly, { duration: 8000 });
        setEhrPushResult({
          status: 'error',
          message: friendly,
          needs_refresh: needsRefresh,
        });
      }
    };

    window.addEventListener('message', handleResponse);
    timeoutId = setTimeout(() => {
      cleanup();
      setEhrPushing(false);
      toast.error('Extension did not respond — ensure v2.1.0+ is installed and the EHR tab is open');
      setEhrPushResult({ status: 'error', message: 'Extension timeout' });
    }, 8000);

    window.postMessage({
      type: 'PINNACLE_PUSH_VIA_EXTENSION',
      requestId,
      targetTabId: targetEhr?.tab_id,
      targetEhrName,
      sections,
      tebra_sections: tebraSections,
      tebra_template: tebraTemplateKey || null,
      tebra_template_labels: tebraCurrentTemplate ? tebraCurrentTemplate.labels : null,
      content: fullContent,
      mode: 'auto',
    }, '*');
    return undefined;
  };

  // Secondary action — also push billing codes to Tebra PM SOAP API
  const handlePushTebraBilling = async () => {
    setEhrPushing(true);
    try {
      const res = await axios.post(`${API}/ehr/push`, {
        session_id: sessionId,
        ...ehrOptions,
        ehr_system: 'tebra',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEhrPushResult(res.data);
      toast.success(res.data.message || 'Billing pushed to Tebra PM');
      const hist = await axios.get(`${API}/ehr/push-history/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEhrPushHistory(hist.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tebra PM billing push failed');
    } finally {
      setEhrPushing(false);
    }
  };

  return {
    // dialog + selectors
    ehrDialogOpen, setEhrDialogOpen,
    ehrPushing,
    ehrPushResult,
    ehrSystem, setEhrSystem,
    tebraStatus,
    testingTebra,
    ehrOptions, setEhrOptions,
    ehrPreview,
    ehrPushHistory,
    tebraEhrData,
    detectedEHRs,
    extensionActive,
    ehrHealth,

    // template state
    tebraTemplates,
    tebraTemplateKey, setTebraTemplateKey,
    tebraAutoDetected,
    tebraCurrentTemplate,
    TEBRA_SECTION_ORDER,
    tebraPreviewSections, setTebraPreviewSections,
    tebraPreviewLoading,
    tebraExpanded, setTebraExpanded,
    tebraFullTemplateNote,
    tebraTemplateSearch, setTebraTemplateSearch,

    // actions
    loadTebraPreview,
    reloadTemplates,
    handleOpenEhrPush,
    handleTestTebra,
    handleEhrPush,
    handlePushTebraBilling,
  };
}
